import React, {
  useEffect,
  useCallback,
  Fragment,
  useMemo,
  useState
} from "react";
import { useTable, useFilters, useGlobalFilter } from "react-table";
import classnames from "classnames";
import Controls from "components/Table/Controls";
import { fuzzyTextFilterFn } from "./utils";
import get from "lodash/get";
import { LIST_PRESENTATION, UI_THEME } from "utils/constants/ui";
import { prepareOptionsList } from "utils/options";
import { useViewport } from "utils/context";
import SlimCTA from "components/SlimCTA";
import LoadMoreFooter from "components/LoadMoreFooter";
import { NO_OP } from "utils/action";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { useResizeDetector } from "react-resize-detector";

const getCellProps = ({
  cell,
  width,
  config,
  cellIx,
  cellsCount,
  hasClickHandler
}) => {
  const configRoot = `styles.virtualized.${cell.column.id}`;
  const itemWidthPercent = get(config, `${configRoot}.widthPercent`);
  const itemWidth = get(config, `${configRoot}.width`);
  const result = {
    style: {
      width: "100%"
    },
    className: ""
  };
  const hasPxWidth = Boolean(itemWidthPercent || itemWidth);
  const isFirstCell = cellIx === 0;
  const isLastCell = cellsCount - 1 === cellIx;
  result.className = classnames(`hairline-1 pa2 ma0`, {
    "bl br": isFirstCell,
    "br": isLastCell || !isFirstCell,
    "pointer": hasClickHandler,
    "flex flex-shrink-0": hasPxWidth
  });

  if (hasPxWidth) {
    const calculatedValue = itemWidthPercent
      ? Math.floor(width * itemWidthPercent)
      : itemWidth;
    result.style.width = `${calculatedValue}px`;
  }

  return result;
};

const getHeaderProps = ({ width, column, config, columnIx, headersCount }) => {
  const isFirst = !columnIx;
  const configRoot = `styles.virtualized.${column.id}`;
  const itemWidthPercent = get(config, `${configRoot}.widthPercent`);
  const itemWidth = get(config, `${configRoot}.width`);

  const result = {
    style: {
      width: "100%"
    }
  };
  const hasPxWidth = Boolean(itemWidthPercent || itemWidth);
  const isLast = headersCount - 1 === columnIx;
  result.className = classnames(
    `fw5 bg-content--secondary f7 f6-ns ph2 pv2 hairline-1 bb tl flex flex-row w-100`,
    {
      "bl br": isFirst,
      "br": !isFirst && !isLast,
      "br br2 br--right br--top br": isLast,
      "flex flex-shrink-0": hasPxWidth
    }
  );
  if (hasPxWidth) {
    const calculatedValue = itemWidthPercent
      ? Math.floor(width * itemWidthPercent)
      : itemWidth;
    result.style.width = `${calculatedValue}px`;
  }

  return result;
};

const VirtualTable = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  width,
  rows,
  rowsCount,
  columnClick,
  config,
  prepareRow
}) => {
  const RenderRow = useCallback(
    ({ index: rowIndex, style }) => {
      const row = rows[rowIndex];
      const isLastRow = rowsCount - 1 === rowIndex;
      prepareRow(row);
      const rowClasses = classnames(
        "hairline-1 bg-white--hover flex flex-row w-100 justify-center items-center",
        {
          "bb": !isLastRow,
          "br--right br--bottom br2": isLastRow
        }
      );
      const rowProps = row.getRowProps({ style });
      rowProps.key = row.values.id;

      return (
        <div {...rowProps} className={rowClasses}>
          {row.cells.map((cell, cellIx) => {
            const columnId = cell.column.id;
            const onClickHandler =
              config.canClickColumn &&
              config.canClickColumn[columnId] &&
              columnClick;
            const hasClickHandler = Boolean(onClickHandler);
            const cellsCount = row.cells.length;

            const cellProps = getCellProps({
              cell,
              width,
              config,
              cellIx,
              cellsCount,
              hasClickHandler
            });

            return (
              <div
                {...cell.getCellProps()}
                {...cellProps}
                onClick={onClickHandler ? () => onClickHandler(cell) : NO_OP}
              >
                <div className="f6 truncate">{cell.render("Cell")}</div>
              </div>
            );
          })}
        </div>
      );
    },
    [prepareRow, rows]
  );

  return (
    <div {...getTableProps()} className="w-100 bt bb hairline-1">
      <div>
        {headerGroups.map((headerGroup) => {
          return (
            <div
              {...headerGroup.getHeaderGroupProps()}
              className="flex flex-row w-100"
            >
              {headerGroup.headers.map((column, columnIx) => {
                const headersCount = headerGroup.headers.length;
                const headerProps = getHeaderProps({
                  column,
                  width,
                  config,
                  headersCount,
                  columnIx
                });

                return (
                  <div {...column.getHeaderProps()} {...headerProps}>
                    <div className="f6">{column.render("Header")}</div>
                    {/* Render the columns filter UI */}
                    <div>
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div {...getTableBodyProps()}>
        <div
          style={{
            height: "calc(100vh - 200px)",
            width: `${width}px`,
            flex: "1 1 auto"
          }}
        >
          <AutoSizer>
            {({ height, width }) => {
              return (
                <List
                  height={height}
                  width={width}
                  itemCount={rowsCount || 0}
                  itemSize={36}
                >
                  {RenderRow}
                </List>
              );
            }}
          </AutoSizer>
        </div>
      </div>
    </div>
  );
};

const RealTable = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  columnWidthPct,
  loadCount,
  rows,
  rowsCount,
  columnClick,
  config,
  prepareRow
}) => {
  return (
    <table
      {...getTableProps()}
      className="w-100 bt hairline-1"
      cellSpacing="0"
      style={{
        tableLayout: "fixed",
        borderSpacing: 0
      }}
    >
      <thead>
        {headerGroups.map((headerGroup) => {
          return (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, columnIx) => {
                const isFirst = !columnIx;
                const cellStyles = get(config, `styles.${column.id}`, {});

                const isLast = headerGroup.headers.length - 1 === columnIx;
                const columnClasses = classnames(
                  `fw5 bg-content--secondary f7 f6-ns ph2 pv2 hairline-1 bb tl`,
                  {
                    "bl br": isFirst,
                    "br br2 br--right br--top br": isLast
                  }
                );

                const headerProps = column.getHeaderProps();

                return (
                  <th
                    {...headerProps}
                    className={columnClasses}
                    style={{
                      width: `${columnWidthPct}%`,
                      ...cellStyles
                    }}
                  >
                    <div className="f6">{column.render("Header")}</div>
                    {/* Render the columns filter UI */}
                    <div>
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          );
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.slice(0, loadCount).map((row, rowIndex) => {
          const isLastRow = rowsCount - 1 === rowIndex;

          prepareRow(row);
          const rowClasses = classnames("hairline-1 bb bg-white--hover", {
            "br--right br--bottom br2": isLastRow
          });
          const rowProps = row.getRowProps();

          return (
            <tr {...rowProps} className={rowClasses}>
              {row.cells.map((cell, cellIx) => {
                const columnId = cell.column.id;
                const cellStyles = get(config, `styles.${cell.column.id}`, {});
                const isFirstCell = cellIx === 0;
                const cellsCount = row.cells.length;
                const isLastCell = cellsCount - 1 === cellIx;
                const onClickHandler =
                  config.canClickColumn &&
                  config.canClickColumn[columnId] &&
                  columnClick;
                const cellClasses = classnames(`hairline-1 bb pa2 ma0`, {
                  "bl br": isFirstCell,
                  "br": isLastCell || !isFirstCell,
                  "pointer": Boolean(onClickHandler)
                });

                return (
                  <td
                    {...cell.getCellProps()}
                    className={cellClasses}
                    style={{
                      width: `${columnWidthPct}%`,
                      ...cellStyles
                    }}
                    onClick={
                      config.canClickColumn &&
                      config.canClickColumn[columnId] &&
                      columnClick
                        ? () => columnClick(cell)
                        : NO_OP
                    }
                  >
                    <div className="f6 truncate">{cell.render("Cell")}</div>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const Table = ({
  config,
  rows: initialData,
  columnClick,
  hoistSetFilter,
  exportConfig,
  scrollTopOnChange
}) => {
  const isVirtualized = Boolean(config.virtualized);
  const { width: theWidth, ref } = useResizeDetector();
  const width = Math.floor(theWidth) || 0;

  const columns = useMemo(() => config.columns, []);
  const data = useMemo(() => initialData, []);
  const [loadCount, setLoadCount] = useState(25);
  const { isSmall } = useViewport();

  const [presentation, setPresentation] = useState(LIST_PRESENTATION.TABLE);
  useEffect(() => {
    if (isSmall && presentation === LIST_PRESENTATION.TABLE) {
      setPresentation(LIST_PRESENTATION.LIST);
    }
  }, [isSmall]);

  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rowData, id, filterValue) => {
        return rowData.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      }
    }),
    []
  );

  const defaultColumn = useMemo(
    () => ({
      /**
       * Pass null for default filter
       */
      Filter: () => null
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    setFilter,
    prepareRow,
    state,
    setAllFilters,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      /**
       * hiddenColumns: [columnKey],
       * filters: [{ id, value }]
       */
      initialState: get(config, "initialState", {})
    },
    useFilters, // useFilters!
    useGlobalFilter // useGlobalFilter!
  );

  const search = state && state.globalFilter;
  const rowsCount = rows.length;
  const columnWidthPct = 100 / config.columns.length;

  useEffect(() => {
    if (setFilter && hoistSetFilter) {
      hoistSetFilter(setFilter);
    }
  }, [setFilter]);

  let content = null;
  const tableProps = {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    columnWidthPct,
    loadCount,
    width,
    rows,
    rowsCount,
    columnClick,
    config,
    prepareRow
  };
  if (isVirtualized) {
    /**
     * Only render when width is a valid non-zero value
     */
    content = tableProps.width ? <VirtualTable {...tableProps} /> : null;
  } else {
    content = <RealTable {...tableProps} />;
  }

  if (rowsCount === 0) {
    const hasFilters = state.filters.length > 0;
    const ctaProps = {
      header: "No rows"
    };
    if (search || hasFilters) {
      ctaProps.header = "No results";
      ctaProps.subheader = "Clear your search and filters to reset";
      ctaProps.actions = [
        {
          copy: "Reset",
          theme: UI_THEME.SLIM,
          onClick: () => {
            setGlobalFilter("");
            setAllFilters([]);
          }
        }
      ];
    }

    content = <SlimCTA {...ctaProps} />;
  } else if (
    presentation !== LIST_PRESENTATION.TABLE &&
    config.components &&
    config.components[presentation]
  ) {
    const ListComponent = config.components[presentation];

    const models = prepareOptionsList({
      models: rows.map((row) => row.original),
      loadCount
    });

    content = <ListComponent models={models} />;
  }

  return (
    <Fragment>
      <div className="pa3 w-100 sticky__header sticky bg-white z-5 top-0 bb hairline-1">
        <Controls
          config={config}
          setAllFilters={setAllFilters}
          data={initialData}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          setFilter={setFilter}
          state={state}
          rows={rows}
          setPresentation={setPresentation}
          presentation={presentation}
          preGlobalFilteredRow={preGlobalFilteredRows}
          exportConfig={exportConfig}
          scrollTopOnChange={scrollTopOnChange}
        />
      </div>
      <div
        ref={ref}
        className={
          config.virtualized ? "pa2 w-100" : "pa3 w-100 overflow-x-scroll"
        }
      >
        {content}
      </div>
      <LoadMoreFooter
        setLoadCount={setLoadCount}
        initialCount={initialData.length}
        rowsCount={rowsCount}
        loadCount={loadCount}
        search={search}
        virtualized={isVirtualized}
      />
    </Fragment>
  );
};

export default Table;
